import { useEffect, useRef, useState } from "react";
import "@/App.css";
import { motion, useScroll, useTransform, useInView } from "framer-motion";
import { Crown, Star, Sparkles, ArrowRight, ChevronDown, Instagram, Mail, Phone, Play, X } from "lucide-react";

// Assets
const LOGO = "https://customer-assets.emergentagent.com/job_elite-consulting-10/artifacts/s3uglxui_IMG_2194.jpg";
const EXPERT_AWARD = "https://customer-assets.emergentagent.com/job_elite-consulting-10/artifacts/4ka8yeo5_21BE43CB-3321-4C87-8132-2C301708BDE6%202.JPG";
const EXPERT_CELEB = "https://customer-assets.emergentagent.com/job_elite-consulting-10/artifacts/8q26urke_DC660060-F250-43F6-AD29-75E47FF4E7F3.JPG";
const VIDEO_ID = "FhefNPsCQhg";
const CALENDLY_LINK = "https://calendly.com/thevisibility9/queens-visibility-strategic-call";

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.15 } }
};

// Animated Section Component
const AnimatedSection = ({ children, className = "" }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  
  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      variants={staggerContainer}
      className={className}
    >
      {children}
    </motion.div>
  );
};

// Navigation
const Navigation = () => {
  const [scrolled, setScrolled] = useState(false);
  
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${scrolled ? 'nav-blur py-4' : 'py-6 bg-transparent'}`}>
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between">
        <a href="#hero" data-testid="nav-logo" className="text-xl md:text-2xl tracking-widest" style={{ fontFamily: "'Quicksand', sans-serif", fontWeight: 400, background: 'linear-gradient(180deg, #D4AF37 0%, #C9A227 50%, #A88B1E 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
          the Visibility<span className="text-xs align-super" style={{ WebkitTextFillColor: '#D4AF37' }}>™</span>
        </a>
        <a 
          href="#booking"
          data-testid="nav-cta-btn"
          className="btn-luxury-outline text-xs md:text-sm px-4 md:px-8 py-3"
        >
          Apply Now
        </a>
      </div>
    </nav>
  );
};

// Hero Section
const HeroSection = () => {
  const { scrollYProgress } = useScroll();
  const y = useTransform(scrollYProgress, [0, 1], [0, 300]);
  
  return (
    <section id="hero" className="relative min-h-screen flex items-center justify-center hero-pattern overflow-hidden" data-testid="hero-section">
      <motion.div 
        style={{ y }}
        className="absolute inset-0 opacity-10"
      >
        <div className="absolute top-20 left-10 w-64 h-64 bg-gold/20 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-gold/10 rounded-full blur-3xl" />
      </motion.div>
      
      <div className="relative z-10 max-w-5xl mx-auto px-6 text-center pt-24">
        <AnimatedSection>
          <motion.div variants={fadeInUp} className="mb-8">
            <Crown className="w-16 h-16 md:w-20 md:h-20 text-gold mx-auto crown-float" />
          </motion.div>
          
          <motion.p variants={fadeInUp} className="text-gold text-sm md:text-base tracking-[0.3em] uppercase mb-6">
            Exclusively for Mrs Beauty Pageant Queens
          </motion.p>
          
          <motion.h1 variants={fadeInUp} className="text-4xl sm:text-5xl md:text-7xl lg:text-8xl font-heading font-medium tracking-tight leading-[1.1] mb-8">
            Your Crown Was Just <br />
            <span className="text-gold-gradient">The Beginning</span>
          </motion.h1>
          
          <motion.p variants={fadeInUp} className="text-lg md:text-xl lg:text-2xl text-luxury-ivory/70 font-light max-w-3xl mx-auto mb-12 leading-relaxed">
            Transform from a recognized pageant queen into a celebrity-positioned, high-authority woman of influence.
          </motion.p>
          
          <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <a 
              href="#booking" 
              data-testid="hero-cta-btn"
              className="btn-luxury inline-flex items-center gap-3"
            >
              Step Into Your Queen Era
              <ArrowRight className="w-5 h-5" />
            </a>
            <a 
              href="#program" 
              data-testid="hero-learn-more-btn"
              className="btn-luxury-outline inline-flex items-center gap-3"
            >
              Learn More
            </a>
          </motion.div>
        </AnimatedSection>
        
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5 }}
          className="absolute bottom-10 left-1/2 -translate-x-1/2"
        >
          <ChevronDown className="w-8 h-8 text-gold/50 animate-bounce" />
        </motion.div>
      </div>
    </section>
  );
};

// Identity Shift Section
const IdentityShiftSection = () => {
  const transformations = [
    { from: "Pageant Title Holder", to: "Recognized Authority" },
    { from: "One-Time Spotlight", to: "Sustained Visibility" },
    { from: "Applause on Stage", to: "Influence in Real Life" },
    { from: "Known Face", to: "Respected Name" },
  ];
  
  return (
    <section className="section-padding bg-luxury-surface grid-pattern" data-testid="identity-section">
      <div className="section-container">
        <AnimatedSection className="text-center mb-20">
          <motion.p variants={fadeInUp} className="text-gold text-sm tracking-[0.3em] uppercase mb-4">
            The Transformation
          </motion.p>
          <motion.h2 variants={fadeInUp} className="text-3xl md:text-4xl lg:text-5xl font-heading mb-6">
            From Recognition to <span className="text-gold">Revolution</span>
          </motion.h2>
          <motion.div variants={fadeInUp} className="luxury-divider mx-auto" />
        </AnimatedSection>
        
        <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {transformations.map((item, index) => (
            <AnimatedSection key={index}>
              <motion.div 
                variants={fadeInUp}
                className="luxury-card p-8 group"
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <p className="text-luxury-ivory/50 text-sm mb-2">From</p>
                    <p className="text-lg md:text-xl text-luxury-ivory/70">{item.from}</p>
                  </div>
                  <ArrowRight className="w-6 h-6 text-gold mx-4 group-hover:translate-x-2 transition-transform" />
                  <div className="flex-1 text-right">
                    <p className="text-gold text-sm mb-2">To</p>
                    <p className="text-lg md:text-xl text-gold font-medium">{item.to}</p>
                  </div>
                </div>
              </motion.div>
            </AnimatedSection>
          ))}
        </div>
      </div>
    </section>
  );
};

// Problem Section
const ProblemSection = () => {
  const painPoints = [
    "After the crown… silence.",
    "You are admired, but not leveraged.",
    "You are seen… but not positioned.",
    "Your title deserves a bigger platform.",
    "Your presence deserves power, not just praise.",
  ];
  
  return (
    <section className="section-padding bg-luxury-surface" data-testid="problem-section">
      <div className="section-container">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <AnimatedSection>
            <motion.div variants={fadeInUp}>
              <span className="quote-mark">"</span>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading leading-tight mb-8">
                We Know Your <span className="text-gold">Unspoken</span> Truth
              </h2>
              <p className="text-lg text-luxury-ivory/60 font-accent italic">
                The crown glitters. The applause fades. And somewhere between the stage and real life, your presence begins to dim.
              </p>
            </motion.div>
          </AnimatedSection>
          
          <AnimatedSection>
            <div className="space-y-6">
              {painPoints.map((point, index) => (
                <motion.div 
                  key={index}
                  variants={fadeInUp}
                  className="flex items-start gap-4 group"
                >
                  <Star className="w-5 h-5 text-gold flex-shrink-0 mt-1 opacity-50 group-hover:opacity-100 transition-opacity" />
                  <p className="text-lg md:text-xl text-luxury-ivory/70 group-hover:text-luxury-ivory transition-colors">
                    {point}
                  </p>
                </motion.div>
              ))}
            </div>
          </AnimatedSection>
        </div>
      </div>
    </section>
  );
};

// Program Introduction Section
const ProgramSection = () => {
  return (
    <section id="program" className="section-padding bg-luxury-surface relative overflow-hidden" data-testid="program-section">
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-gold/20 to-transparent" />
      </div>
      
      <div className="section-container relative z-10">
        <AnimatedSection className="text-center max-w-4xl mx-auto">
          <motion.div variants={fadeInUp} className="mb-8">
            <Sparkles className="w-12 h-12 text-gold mx-auto" />
          </motion.div>
          
          <motion.p variants={fadeInUp} className="text-gold text-sm tracking-[0.3em] uppercase mb-4">
            Introducing
          </motion.p>
          
          <motion.h2 variants={fadeInUp} className="text-4xl md:text-5xl lg:text-6xl font-heading mb-8">
            The <span className="text-gold-gradient">Queens</span> Visibility Program
          </motion.h2>
          
          <motion.div variants={fadeInUp} className="luxury-divider mx-auto mb-8" />
          
          <motion.p variants={fadeInUp} className="text-lg md:text-xl text-luxury-ivory/70 leading-relaxed mb-8">
            A private, elite, curated visibility ecosystem designed exclusively for Mrs category queens. 
            Not for beginners. Not for mass audience. Only for women ready to be seen as Icons, Authorities, 
            Influencers, and Celebrity Personal Brands.
          </motion.p>
          
          <motion.div variants={fadeInUp} className="flex flex-wrap justify-center gap-4 text-sm text-luxury-ivory/50">
            <span className="px-4 py-2 border border-gold/30 rounded-none">Private</span>
            <span className="px-4 py-2 border border-gold/30 rounded-none">Elite</span>
            <span className="px-4 py-2 border border-gold/30 rounded-none">Curated</span>
            <span className="px-4 py-2 border border-gold/30 rounded-none">Exclusive</span>
          </motion.div>
        </AnimatedSection>
      </div>
    </section>
  );
};

// Outcomes Section
const OutcomesSection = () => {
  const outcomes = [
    { title: "Authority", desc: "In your field of work" },
    { title: "Influence", desc: "In elite circles" },
    { title: "Celebrity-Grade", desc: "Personal image" },
    { title: "High-Status", desc: "Perception" },
    { title: "Recognition", desc: "Respect & recall" },
    { title: "Opportunities", desc: "That chase you" },
  ];
  
  return (
    <section className="section-padding" data-testid="outcomes-section">
      <div className="section-container">
        <AnimatedSection className="text-center mb-16">
          <motion.p variants={fadeInUp} className="text-gold text-sm tracking-[0.3em] uppercase mb-4">
            What We Build For You
          </motion.p>
          <motion.h2 variants={fadeInUp} className="text-3xl md:text-4xl lg:text-5xl font-heading">
            Your <span className="text-gold">Outcomes</span>
          </motion.h2>
        </AnimatedSection>
        
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {outcomes.map((item, index) => (
            <AnimatedSection key={index}>
              <motion.div 
                variants={fadeInUp}
                className="luxury-card p-8 text-center group hover:gold-glow"
              >
                <h3 className="text-2xl md:text-3xl font-heading text-gold mb-2">{item.title}</h3>
                <p className="text-luxury-ivory/60">{item.desc}</p>
              </motion.div>
            </AnimatedSection>
          ))}
        </div>
      </div>
    </section>
  );
};

// Highlights Section (Tetris Grid)
const HighlightsSection = () => {
  const highlights = [
    { 
      title: "Power-Packed Events", 
      desc: "Access to high-profile, glamorous, invitation-only events that position you among the elite.",
      span: "lg:col-span-2"
    },
    { 
      title: "Elite Places", 
      desc: "Strategic presence where decisions are made.",
      span: ""
    },
    { 
      title: "Image Building", 
      desc: "Elevate your brand, your business, your personal identity.",
      span: ""
    },
    { 
      title: "Custom Strategy", 
      desc: "No templates. No one-size-fits-all. Your visibility, your way.",
      span: "lg:col-span-2"
    },
  ];
  
  return (
    <section className="section-padding bg-luxury-surface" data-testid="highlights-section">
      <div className="section-container">
        <AnimatedSection className="text-center mb-16">
          <motion.p variants={fadeInUp} className="text-gold text-sm tracking-[0.3em] uppercase mb-4">
            Program Highlights
          </motion.p>
          <motion.h2 variants={fadeInUp} className="text-3xl md:text-4xl lg:text-5xl font-heading">
            What Sets Us <span className="text-gold">Apart</span>
          </motion.h2>
        </AnimatedSection>
        
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {highlights.map((item, index) => (
            <AnimatedSection key={index} className={item.span}>
              <motion.div 
                variants={fadeInUp}
                className="luxury-card p-8 md:p-12 h-full flex flex-col justify-between"
              >
                <div>
                  <Crown className="w-8 h-8 text-gold mb-6" />
                  <h3 className="text-xl md:text-2xl font-heading text-luxury-ivory mb-4">{item.title}</h3>
                  <p className="text-luxury-ivory/60">{item.desc}</p>
                </div>
              </motion.div>
            </AnimatedSection>
          ))}
        </div>
      </div>
    </section>
  );
};

// Strategy Section
const StrategySection = () => {
  const pillars = [
    "Your Personality",
    "Your Industry",
    "Your Ambition",
    "Your Legacy"
  ];
  
  return (
    <section className="section-padding" data-testid="strategy-section">
      <div className="section-container">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <AnimatedSection>
            <motion.p variants={fadeInUp} className="text-gold text-sm tracking-[0.3em] uppercase mb-4">
              Customized For You
            </motion.p>
            <motion.h2 variants={fadeInUp} className="text-3xl md:text-4xl lg:text-5xl font-heading mb-8">
              Your <span className="text-gold">Visibility</span> Roadmap
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-lg text-luxury-ivory/70 mb-8 leading-relaxed">
              Each Queen receives a tailor-made visibility strategy designed around who she is and where she's going. 
              We focus on long-term influence, sustainable authority, and elite positioning.
            </motion.p>
            <motion.a 
              variants={fadeInUp}
              href="#booking"
              data-testid="strategy-cta-btn"
              className="btn-luxury inline-flex items-center gap-3"
            >
              Get Your Strategy
              <ArrowRight className="w-5 h-5" />
            </motion.a>
          </AnimatedSection>
          
          <AnimatedSection>
            <div className="grid grid-cols-2 gap-4">
              {pillars.map((pillar, index) => (
                <motion.div 
                  key={index}
                  variants={fadeInUp}
                  className="glass-card p-6 text-center"
                >
                  <p className="text-gold font-heading text-lg">{pillar}</p>
                </motion.div>
              ))}
            </div>
          </AnimatedSection>
        </div>
      </div>
    </section>
  );
};

// Expert Section
const ExpertSection = () => {
  const [showVideo, setShowVideo] = useState(false);
  
  return (
    <section className="section-padding bg-luxury-surface" data-testid="expert-section">
      <div className="section-container">
        <AnimatedSection className="text-center mb-16">
          <motion.p variants={fadeInUp} className="text-gold text-sm tracking-[0.3em] uppercase mb-4">
            Meet Your Visibility Strategist
          </motion.p>
          <motion.h2 variants={fadeInUp} className="text-3xl md:text-4xl lg:text-5xl font-heading">
            <span className="text-gold">Vijay</span> Kadechkarr
          </motion.h2>
        </AnimatedSection>
        
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <AnimatedSection>
            <div className="grid grid-cols-2 gap-4">
              <motion.div variants={fadeInUp} className="image-luxury rounded-none overflow-hidden">
                <img 
                  src={EXPERT_AWARD} 
                  alt="Vijay Kadechkarr - India's Fastest Growing Leader" 
                  className="w-full h-64 md:h-80 object-cover transition-transform duration-700"
                />
              </motion.div>
              <motion.div variants={fadeInUp} className="image-luxury rounded-none overflow-hidden mt-8">
                <img 
                  src={EXPERT_CELEB} 
                  alt="Vijay Kadechkarr with celebrities" 
                  className="w-full h-64 md:h-80 object-cover transition-transform duration-700"
                />
              </motion.div>
            </div>
          </AnimatedSection>
          
          <AnimatedSection>
            <motion.div variants={fadeInUp} className="space-y-6">
              <p className="text-gold text-sm tracking-[0.2em] uppercase">
                India's 1st Visibility Strategist
              </p>
              <p className="text-lg md:text-xl text-luxury-ivory/80 leading-relaxed">
                Vijay Kadechkarr is a strategic visibility architect trusted by high-value individuals, brands, and public figures who don't just want attention—they want influence that converts into power, credibility, and revenue.
              </p>
              <p className="text-luxury-ivory/60 leading-relaxed">
                As the founder of The Visibility, Vijay has helped entrepreneurs, actors, politicians, authors, and luxury brands engineer their public image, command authority in their industry, and attract premium opportunities through precision-driven branding, media positioning, and high-impact visibility strategies.
              </p>
              <blockquote className="border-l-2 border-gold pl-6 py-4">
                <p className="text-luxury-ivory/80 font-accent italic text-lg">
                  "He Helps You Build the Visibility You Deserve."
                </p>
              </blockquote>
              
              <button 
                onClick={() => setShowVideo(true)}
                data-testid="expert-video-btn"
                className="btn-luxury-outline inline-flex items-center gap-3 mt-4"
              >
                <Play className="w-5 h-5" />
                Watch Video
              </button>
            </motion.div>
          </AnimatedSection>
        </div>
      </div>
      
      {/* Video Modal */}
      {showVideo && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-6" data-testid="video-modal">
          <button 
            onClick={() => setShowVideo(false)}
            data-testid="video-modal-close"
            className="absolute top-6 right-6 text-white hover:text-gold transition-colors"
          >
            <X className="w-8 h-8" />
          </button>
          <div className="w-full max-w-4xl aspect-video">
            <iframe 
              width="100%" 
              height="100%" 
              src={`https://www.youtube.com/embed/${VIDEO_ID}?autoplay=1`}
              title="Vijay Kadechkarr Promo Video"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="rounded-none"
            />
          </div>
        </div>
      )}
    </section>
  );
};

// Emotional Reinforcement Section
const EmotionalSection = () => {
  const quotes = [
    "Your crown was just the beginning.",
    "Queens don't disappear after the spotlight.",
    "Legacy is built when visibility is intentional.",
    "You were crowned to lead, not to fade.",
  ];
  
  return (
    <section className="section-padding relative overflow-hidden" data-testid="emotional-section">
      <div className="absolute inset-0 bg-gradient-to-b from-luxury-black via-luxury-surface to-luxury-black opacity-50" />
      
      <div className="section-container relative z-10">
        <AnimatedSection className="max-w-4xl mx-auto">
          {quotes.map((quote, index) => (
            <motion.div 
              key={index}
              variants={fadeInUp}
              className="text-center py-8 md:py-12 border-b border-white/5 last:border-0"
            >
              <p className="text-2xl md:text-3xl lg:text-4xl font-heading text-luxury-ivory/90 leading-relaxed">
                "{quote}"
              </p>
            </motion.div>
          ))}
        </AnimatedSection>
      </div>
    </section>
  );
};

// Exclusivity Section
const ExclusivitySection = () => {
  return (
    <section className="section-padding bg-luxury-surface" data-testid="exclusivity-section">
      <div className="section-container">
        <div className="max-w-3xl mx-auto text-center">
          <AnimatedSection>
            <motion.div variants={fadeInUp} className="mb-8">
              <Crown className="w-16 h-16 text-gold mx-auto" />
            </motion.div>
            
            <motion.h2 variants={fadeInUp} className="text-3xl md:text-4xl lg:text-5xl font-heading mb-8">
              This Is <span className="text-gold">Not</span> For Everyone
            </motion.h2>
            
            <motion.div variants={fadeInUp} className="luxury-divider mx-auto mb-8" />
            
            <motion.div variants={fadeInUp} className="space-y-4 mb-12">
              <p className="text-lg text-luxury-ivory/70">Limited Intake • Invitation-Only • Curated Selection</p>
              <p className="text-luxury-ivory/60">
                Designed for women who value class, privacy, power, and precision. 
                If you're ready to command your influence, this is your moment.
              </p>
            </motion.div>
            
            <motion.a 
              variants={fadeInUp}
              href="#booking"
              data-testid="exclusivity-cta-btn"
              className="btn-luxury inline-flex items-center gap-3"
            >
              Apply to Enter
              <ArrowRight className="w-5 h-5" />
            </motion.a>
          </AnimatedSection>
        </div>
      </div>
    </section>
  );
};

// Calendly Booking Section
const BookingSection = () => {
  useEffect(() => {
    // Load Calendly script
    const script = document.createElement('script');
    script.src = 'https://assets.calendly.com/assets/external/widget.js';
    script.async = true;
    document.body.appendChild(script);
    
    return () => {
      document.body.removeChild(script);
    };
  }, []);
  
  return (
    <section id="booking" className="section-padding" data-testid="booking-section">
      <div className="section-container">
        <AnimatedSection className="text-center mb-12">
          <motion.p variants={fadeInUp} className="text-gold text-sm tracking-[0.3em] uppercase mb-4">
            Your Next Step
          </motion.p>
          <motion.h2 variants={fadeInUp} className="text-3xl md:text-4xl lg:text-5xl font-heading mb-6">
            Book Your <span className="text-gold">Strategic Call</span>
          </motion.h2>
          <motion.p variants={fadeInUp} className="text-lg text-luxury-ivory/60 max-w-2xl mx-auto">
            This is where Queens become Icons. Claim your authority. Command your influence.
          </motion.p>
        </AnimatedSection>
        
        <AnimatedSection>
          <motion.div variants={fadeInUp} className="max-w-4xl mx-auto calendly-container">
            <div 
              className="calendly-inline-widget" 
              data-url={`${CALENDLY_LINK}?hide_gdpr_banner=1&background_color=0a0a0a&text_color=f5f5f0&primary_color=d4af37`}
              style={{ minWidth: '320px', height: '700px' }}
              data-testid="calendly-widget"
            />
          </motion.div>
        </AnimatedSection>
      </div>
    </section>
  );
};

// Final CTA Section
const FinalCTASection = () => {
  return (
    <section className="section-padding bg-luxury-surface relative overflow-hidden" data-testid="final-cta-section">
      <div className="absolute inset-0">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gold/5 rounded-full blur-3xl" />
      </div>
      
      <div className="section-container relative z-10 text-center">
        <AnimatedSection>
          <motion.div variants={fadeInUp}>
            <Crown className="w-20 h-20 text-gold mx-auto mb-8 crown-float" />
          </motion.div>
          
          <motion.h2 variants={fadeInUp} className="text-4xl md:text-5xl lg:text-6xl font-heading mb-8">
            This Is Where Queens <br />
            <span className="text-gold-gradient">Become Icons</span>
          </motion.h2>
          
          <motion.p variants={fadeInUp} className="text-lg md:text-xl text-luxury-ivory/60 max-w-2xl mx-auto mb-12">
            Step into your Queen era. Build your celebrity authority. Command your influence.
          </motion.p>
          
          <motion.a 
            variants={fadeInUp}
            href="#booking"
            data-testid="final-cta-btn"
            className="btn-luxury inline-flex items-center gap-3 text-base md:text-lg px-10 py-5"
          >
            Claim Your Authority
            <ArrowRight className="w-6 h-6" />
          </motion.a>
        </AnimatedSection>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  return (
    <footer className="py-16 px-6 md:px-12 border-t border-white/10" data-testid="footer">
      <div className="max-w-7xl mx-auto">
        <div className="grid md:grid-cols-3 gap-12 mb-12">
          <div>
            <h3 className="text-2xl md:text-3xl tracking-widest mb-4" style={{ fontFamily: "'Quicksand', sans-serif", fontWeight: 400, background: 'linear-gradient(180deg, #D4AF37 0%, #C9A227 50%, #A88B1E 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
              the Visibility<span className="text-sm align-super" style={{ WebkitTextFillColor: '#D4AF37' }}>™</span>
            </h3>
            <p className="text-luxury-ivory/50 text-sm">
              Authority | Influence | Access
            </p>
          </div>
          
          <div>
            <h4 className="text-gold text-sm tracking-[0.2em] uppercase mb-6">Contact</h4>
            <div className="space-y-4">
              <a 
                href="mailto:apply@queensvisibility.pro" 
                data-testid="footer-email"
                className="flex items-center gap-3 text-luxury-ivory/70 hover:text-gold transition-colors"
              >
                <Mail className="w-5 h-5" />
                apply@queensvisibility.pro
              </a>
              <a 
                href="tel:+919076050999" 
                data-testid="footer-phone"
                className="flex items-center gap-3 text-luxury-ivory/70 hover:text-gold transition-colors"
              >
                <Phone className="w-5 h-5" />
                +91 9076050999
              </a>
            </div>
          </div>
          
          <div>
            <h4 className="text-gold text-sm tracking-[0.2em] uppercase mb-6">Follow</h4>
            <a 
              href="https://www.instagram.com/vijayvisibility" 
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-instagram"
              className="flex items-center gap-3 text-luxury-ivory/70 hover:text-gold transition-colors"
            >
              <Instagram className="w-5 h-5" />
              @vijayvisibility
            </a>
          </div>
        </div>
        
        <div className="pt-8 border-t border-white/10 text-center">
          <p className="text-luxury-ivory/30 text-sm">
            © {new Date().getFullYear()} The Visibility. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};

// Main App Component
function App() {
  return (
    <div className="bg-luxury-black min-h-screen">
      <Navigation />
      <HeroSection />
      <IdentityShiftSection />
      <ProblemSection />
      <ProgramSection />
      <OutcomesSection />
      <HighlightsSection />
      <StrategySection />
      <ExpertSection />
      <EmotionalSection />
      <ExclusivitySection />
      <BookingSection />
      <FinalCTASection />
      <Footer />
    </div>
  );
}

export default App;
